import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../utils/axios";
import { ExpenseFilters, ExpensePayment, CreateExpensePaymentRequest, ExpenseCategory, Vendor, ExpenseFormDataNew } from "../../utils/expense.types";
import { IDeleteApiResponse, IError } from "../../utils/api.types";
import { fetchSuppliers } from '../supplierStore/supplierThunk';

export const fetchExpensePayments = createAsyncThunk(
    'expense/fetchExpensePayments',
    async (filters: ExpenseFilters): Promise<ExpensePayment[]> => {
        let paramsArray = [];
        
        if (filters.vendor) {
            let q1 = ['supplier', filters.vendor];
            paramsArray.push(q1);
        }

        if (filters.paymentStatus) {
            let q2 = ['paymentStatus', filters.paymentStatus];
            paramsArray.push(q2);
        }

        if (filters.paymentMode) {
            let q3 = ['paymentMode', filters.paymentMode];
            paramsArray.push(q3);
        }

        if (filters.expenseCategory) {
            let q4 = ['expenseCategory', filters.expenseCategory];
            paramsArray.push(q4);
        }

        if (filters.amountFrom && filters.amountTo) {
            let q5 = ['fromAmountRange', filters.amountFrom.toString()];
            let q6 = ['toAmountRange', filters.amountTo.toString()];
            paramsArray.push(q5);
            paramsArray.push(q6);
        }

        if (filters.dateFrom) {
            let q7 = ['startDate', new Date(filters.dateFrom).getTime().toString()];
            paramsArray.push(q7);
        }

        if (filters.dateTo) {
            let q8 = ['endDate', new Date(filters.dateTo).getTime().toString()];
            paramsArray.push(q8);
        }

        const params = new URLSearchParams(paramsArray);
       
        const response = await axios.get(`/api/v1/invoice/expense-payments`, {params});
        return response.data.payments || [];
    }
);

export const recordExpensePayment = createAsyncThunk(
    'expense/recordExpensePayment',
    async (data: ExpenseFormDataNew): Promise<ExpensePayment> => {
        const response = await axios.post('/api/v1/invoice/vendor-payment/expense-record', data);
        return response.data.expensePayment;
    }
);

export const deleteExpensePayment = createAsyncThunk<IDeleteApiResponse,
string,
{
    rejectValue: IError,
}
>(
    'expense/deleteExpensePayment',
    async (paymentId: string, thunkApi) => {
        try {
            const response = await axios.delete(`/api/v1/invoice/vendor-payment/delete-entire/${paymentId}`);
            const { data } = response;
            if (response.status === 200) {
                return data;
            }
            return thunkApi.rejectWithValue(data.error);
        } catch (error: any) {
            return thunkApi.rejectWithValue(error.response.data);
        }
    }
);

export const fetchExpenseCategories = createAsyncThunk(
    'expense/fetchExpenseCategories',
    async (params: { flat?: boolean } = {}): Promise<ExpenseCategory[]> => {
        const response = await axios.get('/api/v1/invoice/expense-categories', {
            params: { flat: params.flat !== undefined ? params.flat : false }
        });
        return response.data.data || [];
    }
);

export const exportExpenseReport = createAsyncThunk(
    'expense/exportExpenseReport',
    async (params: { format?: 'excel' | 'pdf'; filters?: ExpenseFilters } = {}): Promise<any> => {
        const queryParams = new URLSearchParams();
        queryParams.append('format', params.format || 'excel');
        
        if (params.filters) {
            Object.entries(params.filters).forEach(([key, value]) => {
                if (value) {
                    queryParams.append(key, value.toString());
                }
            });
        }
        
        const response = await axios.get(`/api/v1/invoice/expense-payments/export?${queryParams.toString()}`, {
            responseType: 'blob'
        });
        
        // Create download link
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = `expense-report-${new Date().toISOString().split('T')[0]}.${params.format === 'pdf' ? 'pdf' : 'xlsx'}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        return { success: true };
    }
);

export const fetchExpenseVendors = createAsyncThunk(
    'expense/fetchExpenseVendors',
    async (params: { accountId: string; searchQuery?: string }, thunkApi): Promise<Vendor[]> => {
        const response = await thunkApi.dispatch(fetchSuppliers({ 
            accountId: params.accountId, 
            params: params.searchQuery || '' 
        })).unwrap();
        
        // Transform suppliers to vendor format
        const vendors = response.suppliers?.map((supplier: any) => ({
            _id: supplier._id,
            name: supplier.name,
            legalName: supplier.legalName,
            email: supplier.email,
            phone: supplier.phone,
            isActive: supplier.isActive,
            bankAccounts: supplier.bankAccounts || []
        })) || [];
        
        return vendors;
    }
);

export const createExpenseCategory = createAsyncThunk(
    'expense/createExpenseCategory',
    async (categoryData: {
        name: string;
        description?: string;
        code?: string;
        parentCategory?: string;
        color?: string;
        icon?: string;
        sortOrder?: number;
    }): Promise<ExpenseCategory> => {
        // Generate code if not provided
        const code = categoryData.code || categoryData.name.toUpperCase().replace(/\s+/g, '_').substring(0, 20);
        
        const response = await axios.post('/api/v1/invoice/expense-categories', {
            ...categoryData,
            code,
            isActive: true,
            color: categoryData.color || '#6366f1',
            sortOrder: categoryData.sortOrder || 999
        });
        
        return response.data.data;
    }
);